import React, { useState, useEffect, useContext } from 'react';
import './object.css';
import { validateSteps } from "../validateSteps";
import { LanguageContext } from "../context/LanguageContext";

const CreateObjectStep4 = ({
  formData,
  setFormData,
  onNext,
  onBack,
  missingFields = [],
  setMissingFields,
  validationActive
}) => {

  const { language, texts } = useContext(LanguageContext);
  const lang = (language || "de").split("-")[0];

  const [contactEmailError, setContactEmailError] = useState('');
  const [arrivalDays, setArrivalDays] = useState([]);
  const [loading, setLoading] = useState(true);

  // Anreisetage laden
  useEffect(() => {
    const fetchArrivalDays = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/arrival_days?lang=${lang}`
        );

        if (!response.ok) throw new Error(texts.object.step4.errorLoadingArrivalDays);

        const data = await response.json();
        setArrivalDays(data);

      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchArrivalDays();
  }, [lang, texts]);

  // Allgemeine Feldänderungen
  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Ja/Nein Felder
    if (name === "party_allowed" || name === "smoking_allowed") {
      newValue = value === "yes";
    }

    const updated = { ...formData, [name]: newValue };
    setFormData(updated);

    if (validationActive) {
      const { missing } = validateSteps(updated);
      setMissingFields(missing);
    }
  };

  // Anreisetage Checkbox
  const handleCheckboxChange = (e, id) => {
    const { checked } = e.target;

    const updatedArrivalDays = checked
      ? [...formData.arrival_days, id]
      : formData.arrival_days.filter(dayId => dayId !== id);

    const updated = { ...formData, arrival_days: updatedArrivalDays };
    setFormData(updated);

    if (validationActive) {
      const { missing } = validateSteps(updated);
      setMissingFields(missing);
    }
  };

  // E-Mail
  const handleEmailChange = (e) => {
    const updated = { ...formData, contact_email: e.target.value };
    setFormData(updated);

    if (validationActive) {
      const { missing } = validateSteps(updated);
      setMissingFields(missing);
    }
  };

  const handleEmailBlur = () => {
    const email = formData.contact_email || '';
    setContactEmailError(
      !email.includes('@') ? texts.object.step4.invalidEmail : ''
    );
  };

  // Stundenliste
  const hourOptions = Array.from({ length: 24 }, (_, i) => {
    const formattedHour = i < 10 ? `0${i}:00` : `${i}:00`;
    return (
      <option key={i} value={formattedHour}>
        {formattedHour}
      </option>
    );
  });

  return (
    <div className="object-form-container">
      <h2 className="object-header">{texts.object.step4.title}</h2>

      <div className="object-content-scroll-area">
        <div className="form-cards">

          <form className="object-form">

            {/* Maximale Belegung */}
            <div className="object-form-group">
              <label htmlFor="num_peoples" className="objects-form-label">
                {texts.object.step4.maxOccupancy}
              </label>
              <select
                id="num_peoples"
                name="num_peoples"
                value={formData.num_peoples ?? ""}
                onChange={handleChange}
                className={`object-form-input ${
                  validationActive && missingFields.includes("num_peoples") ? "input-error" : ""
                }`}
              >
                <option value="">{texts.object.step4.selectPlaceholder}</option>
                {[...Array(20).keys()].map((i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            {/* Maximale Anzahl Haustiere */}
            <div className="object-form-group">
              <label htmlFor="max_pets" className="objects-form-label">
                {texts.object.step4.maxPets}
              </label>
              <select
                id="max_pets"
                name="max_pets"
                value={formData.max_pets ?? ""}
                onChange={handleChange}
                className={`object-form-input ${
                  validationActive && missingFields.includes("max_pets") ? "input-error" : ""
                }`}
              >
                <option value="">{texts.object.step4.selectPlaceholder}</option>
                <option value="0">{texts.object.step4.no}</option>
                {[...Array(10).keys()].map((i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            {/* Mindestaufenthalt */}
            <div className="object-form-group">
              <label htmlFor="min_stay" className="objects-form-label">
                {texts.object.step4.minNights}
              </label>
              <select
                id="min_stay"
                name="min_stay"
                value={formData.min_stay ?? ""}
                onChange={handleChange}
                className={`object-form-input ${
                  validationActive && missingFields.includes("min_stay") ? "input-error" : ""
                }`}
              >
                <option value="">{texts.object.step4.selectPlaceholder}</option>
                {[...Array(20).keys()].map((i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            {/* Anreisetage */}
            <div className="object-form-group">
              <label>{texts.object.step4.arrivalDays}</label>

              {loading ? (
                <p>Loading…</p>
              ) : (
                <div
                  className={`object-arrivalday-container ${
                    validationActive && missingFields.includes("arrival_days") ? "input-error" : ""
                  }`}
                >
                  {arrivalDays.map((day) => (
                    <label key={day.id} className="object-arrivalday-item">
                      <input
                        type="checkbox"
                        checked={formData.arrival_days?.includes(day.id) || false}
                        onChange={(e) => handleCheckboxChange(e, day.id)}
                      />
                      {day.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Check-in */}
            <div className="object-form-group">
              <label htmlFor="check_in">{texts.object.step4.earliestCheckin}</label>
              <select
                id="check_in"
                name="check_in"
                value={formData.check_in || ""}
                onChange={handleChange}
                className={`object-form-input ${
                  validationActive && missingFields.includes("check_in") ? "input-error" : ""
                }`}
              >
                <option value="">{texts.object.step4.selectPlaceholder}</option>
                {hourOptions}
              </select>
            </div>

            {/* Check-out */}
            <div className="object-form-group">
              <label htmlFor="check_out">{texts.object.step4.latestCheckout}</label>
              <select
                id="check_out"
                name="check_out"
                value={formData.check_out || ""}
                onChange={handleChange}
                className={`object-form-input ${
                  validationActive && missingFields.includes("check_out") ? "input-error" : ""
                }`}
              >
                <option value="">{texts.object.step4.selectPlaceholder}</option>
                {hourOptions}
              </select>
            </div>

            {/* Regeln */}
            <h4 className="object-title">{texts.object.step4.rules}</h4>

            <div className="object-form-group">
              <label className="objects-form-label">{texts.object.step4.partyAllowed}</label>
              <div className="object-yesno-inline">
                <label>
                  <input
                    type="radio"
                    name="party_allowed"
                    value="yes"
                    checked={formData.party_allowed === true}
                    onChange={handleChange}
                  />
                  {texts.object.step4.yes}
                </label>

                <label>
                  <input
                    type="radio"
                    name="party_allowed"
                    value="no"
                    checked={formData.party_allowed === false}
                    onChange={handleChange}
                  />
                  {texts.object.step4.no}
                </label>
              </div>
            </div>

            <div className="object-form-group">
              <label className="objects-form-label">{texts.object.step4.smokingAllowed}</label>
              <div className="object-yesno-inline">
                <label>
                  <input
                    type="radio"
                    name="smoking_allowed"
                    value="yes"
                    checked={formData.smoking_allowed === true}
                    onChange={handleChange}
                  />
                  {texts.object.step4.yes}
                </label>

                <label>
                  <input
                    type="radio"
                    name="smoking_allowed"
                    value="no"
                    checked={formData.smoking_allowed === false}
                    onChange={handleChange}
                  />
                  {texts.object.step4.no}
                </label>
              </div>
            </div>

            {/* Kontaktoptionen */}
            <h4 className="object-title">{texts.object.step4.contactOptions}</h4>

            <div className="object-form-group">
              <label>{texts.object.step4.contactHow}</label>
              <select
                name="contact_method"
                value={formData.contact_method || ""}
                onChange={handleChange}
                className={`object-form-input ${
                  validationActive && missingFields.includes("contact_method") ? "input-error" : ""
                }`}
              >
                <option value="">{texts.object.step4.selectPlaceholder}</option>
                <option value="website">{texts.object.step4.contactWebsite}</option>
                <option value="email">{texts.object.step4.contactEmail}</option>
                <option value="both">{texts.object.step4.contactBoth}</option>
              </select>
            </div>

            {(formData.contact_method === "website" || formData.contact_method === "both") && (
              <div className="object-form-group">
                <label htmlFor="website_url">{texts.object.step4.contactWebsite}</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url || ""}
                  onChange={handleChange}
                  className={`form-input ${
                    validationActive && missingFields.includes("website_url") ? "input-error" : ""
                  }`}
                  placeholder={texts.object.step4.contactWebsitePlaceholder}
                />
              </div>
            )}

            {(formData.contact_method === "email" || formData.contact_method === "both") && (
              <div className="object-form-group">
                <label htmlFor="contact_email">{texts.object.step4.contactEmail}</label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  value={formData.contact_email || ""}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  className={`form-input ${
                    validationActive && missingFields.includes("contact_email") ? "input-error" : ""
                  }`}
                />
              </div>
            )}

            {(formData.contact_method === "email" || formData.contact_method === "both") &&
              contactEmailError && (
                <div className="object-form-group email-error-row">
                  <label className="objects-form-label" />
                  <p className="object-error-message">{contactEmailError}</p>
                </div>
              )}

          </form>

        </div>
      </div>
    </div>
  );
};

export default CreateObjectStep4;
